/**
 * @file menuLabelUtils.js
 * @description Pure utility functions for parsing the client options that control
 * the renamed Individual navigation menu labels (Messages, Benefits, Assets) and
 * whether the Individual Asset menu should be displayed.
 */

import { ClientOptionEnum } from './enums';
import { getText } from '../../i18n';

const DEFAULT_MENU_LABELS = {
    messages: getText('msg_f1702b468627'),
    benefits: getText('msg_927f40673764'),
    assets: getText('msg_20e338624cee'),
};

const findMenuLabelOption = (response, optionId, defaultValue) => {
    const array = Array.isArray(response) ? response : [];
    const option = array.find(item => item.clientOptionId === optionId);
    return option && !option.usingDefaultValue
        ? option.value || defaultValue
        : defaultValue;
};

/**
 * Builds the Individual menu labels map from a client options API response.
 * @param {Array<Object>} response - Response containing RenameMessagesMenu, RenameBenefitsMenu and RenameAssetsMenu options.
 * @returns {{messages: string, benefits: string, assets: string}} Map of menu labels.
 */
const buildMenuLabelsMap = response => ({
    messages: findMenuLabelOption(
        response,
        ClientOptionEnum.RenameMessagesMenu,
        DEFAULT_MENU_LABELS.messages,
    ),
    benefits: findMenuLabelOption(
        response,
        ClientOptionEnum.RenameBenefitsMenu,
        DEFAULT_MENU_LABELS.benefits,
    ),
    assets: findMenuLabelOption(
        response,
        ClientOptionEnum.RenameAssetsMenu,
        DEFAULT_MENU_LABELS.assets,
    ),
});

/**
 * Determines whether the Individual Asset menu should be displayed.
 * @param {Array<Object>} response - Response containing the DisplayIndividualAssetMenu option.
 * @returns {boolean} True if the asset menu should be displayed.
 */
const parseDisplayIndividualAssetMenu = response => {
    const array = Array.isArray(response) ? response : [];
    const option = array.find(
        item =>
            item.clientOptionId === ClientOptionEnum.DisplayIndividualAssetMenu,
    );
    return option ? option.value !== 'false' : false;
};

export {
    DEFAULT_MENU_LABELS,
    buildMenuLabelsMap,
    parseDisplayIndividualAssetMenu,
};
